import React, { useState } from 'react';
import { Building2, CreditCard, FileText, Upload } from 'lucide-react';

// Lo que tiene a mano un paciente cuando termina de transferir: la captura de
// pantalla, la imagen o el PDF que comparte Mercado Pago o el banco, o una
// foto del celular. Antes solo se aceptaba JPG/PNG de hasta 5 MB y lo demás
// terminaba en "no pude subir el comprobante": entre sep y oct de 2026, 9 de
// 40 comprobantes eran PDF y ninguno entró por acá (los adjuntó la secretaria
// a mano, después). El backend ya aceptaba PDF y WEBP.
const TIPO_POR_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  pdf: 'application/pdf',
};
const TIPOS_ACEPTADOS = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_MB = 10; // el mismo tope que paymentVerification.routes.js
// Una foto de la cámara pesa varios MB: se achica en el celular antes de
// subirla, que con datos móviles tarda y además puede pasarse del tope.
const ACHICAR_DESDE_BYTES = 1.5 * 1024 * 1024;
const LADO_MAXIMO_PX = 2000;

// Algunos selectores de archivos (o Drive) no informan el tipo: se deduce de
// la extensión y se rearma el archivo con ese tipo, porque el backend decide
// por el tipo que viaja en la subida. null = no es un comprobante que se pueda
// subir (por ejemplo, una foto HEIC elegida desde Archivos en el iPhone).
const conTipoValido = (file: File): File | null => {
  const declarado = file.type && file.type !== 'application/octet-stream' ? file.type : '';
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  const tipo = declarado || TIPO_POR_EXTENSION[extension] || '';
  if (!TIPOS_ACEPTADOS.includes(tipo)) return null;
  return tipo === file.type ? file : new File([file], file.name, { type: tipo });
};

// 180 KB, 1,2 MB
const tamanoLegible = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toLocaleString('es-AR', { maximumFractionDigits: 1 })} MB`;

const achicarImagen = async (file: File): Promise<File> => {
  const imagen = await createImageBitmap(file);
  const escala = Math.min(1, LADO_MAXIMO_PX / Math.max(imagen.width, imagen.height));
  const lienzo = document.createElement('canvas');
  lienzo.width = Math.round(imagen.width * escala);
  lienzo.height = Math.round(imagen.height * escala);
  const ctx = lienzo.getContext('2d');
  if (!ctx) return file;
  ctx.fillStyle = '#fff'; // un PNG con transparencia no queda con fondo negro
  ctx.fillRect(0, 0, lienzo.width, lienzo.height);
  ctx.drawImage(imagen, 0, 0, lienzo.width, lienzo.height);
  imagen.close();
  const blob = await new Promise<Blob | null>((resolver) => lienzo.toBlob(resolver, 'image/jpeg', 0.85));
  if (!blob || blob.size >= file.size) return file;
  return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
};

interface PaymentStepProps {
  professionalId: number;
  servicePrice: number;
  appointmentId?: number | null;
  onPaymentMethodSelected: (method: string, proofUrl?: string) => void;
  onBack: () => void;
}

const PaymentStep: React.FC<PaymentStepProps> = ({
  professionalId,
  servicePrice,
  appointmentId,
  onPaymentMethodSelected,
  onBack
}) => {
  const [selectedMethod, setSelectedMethod] = useState<'transferencia' | 'mercadopago' | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [preparandoArchivo, setPreparandoArchivo] = useState(false);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofPreview, setProofPreview] = useState<string | null>(null);
  const [loadingMercadoPago, setLoadingMercadoPago] = useState(false);

  // Cargar configuración de pago del profesional
  const [paymentConfig, setPaymentConfig] = useState<any>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  
  // Estados para manejar seña vs precio completo
  const [paymentAmount, setPaymentAmount] = useState<number>(servicePrice);
  const [isDeposit, setIsDeposit] = useState<boolean>(false);
  const [depositPercentage, setDepositPercentage] = useState<number>(0);
  const [fullPrice, setFullPrice] = useState<number>(servicePrice);

  React.useEffect(() => {
    loadPaymentConfig();
    loadPaymentAmount();
  }, [professionalId]);

  const loadPaymentAmount = () => {
    try {
      const pendingBooking = sessionStorage.getItem('pendingBooking');
      if (pendingBooking) {
        const bookingData = JSON.parse(pendingBooking);
        console.log('📊 Datos de reserva para pago:', bookingData);
        
        // Usar el price del bookingData (ya tiene el cálculo correcto de seña/completo)
        if (bookingData.price) {
          setPaymentAmount(bookingData.price);
        }
        
        // Guardar precio completo del servicio
        if (bookingData.fullPrice) {
          setFullPrice(bookingData.fullPrice);
        }
        
        // Verificar si es seña
        if (bookingData.tipoCobro === 'solo_sena' && bookingData.porcentajeSena) {
          setIsDeposit(true);
          setDepositPercentage(bookingData.porcentajeSena);
          console.log(`✅ Cobrando seña de ${bookingData.porcentajeSena}%: $${bookingData.price}`);
        } else {
          console.log(`✅ Cobrando precio completo: $${bookingData.price}`);
        }
      }
    } catch (error) {
      console.error('Error cargando monto de pago:', error);
    }
  };

  const loadPaymentConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/config/${professionalId}`);
      if (response.ok) {
        const data = await response.json();
        setPaymentConfig(data.data);
      }
    } catch (error) {
      console.error('Error cargando config de pagos:', error);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const elegido = input.files?.[0];
    if (!elegido) return;

    const descartar = (mensaje: string) => {
      alert(mensaje);
      input.value = ''; // Limpiar el input
      setProofFile(null);
      setProofPreview(null);
    };

    const archivo = conTipoValido(elegido);
    if (!archivo) {
      descartar('⚠️ Ese archivo no se puede subir. Subí una captura de pantalla, una foto o el PDF del comprobante.');
      return;
    }

    let listo = archivo;
    if (archivo.type.startsWith('image/') && archivo.size > ACHICAR_DESDE_BYTES) {
      setPreparandoArchivo(true);
      listo = await achicarImagen(archivo).catch(() => archivo);
      setPreparandoArchivo(false);
    }

    if (listo.size > MAX_MB * 1024 * 1024) {
      descartar(`⚠️ El archivo pesa más de ${MAX_MB} MB. Probá con una captura de pantalla del comprobante.`);
      return;
    }

    setProofFile(listo);

    // Vista previa: la imagen, o el nombre del archivo si es un PDF
    if (listo.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProofPreview(reader.result as string);
      };
      reader.readAsDataURL(listo);
    } else {
      setProofPreview(null);
    }
  };

  const handleTransferSubmit = async () => {
    if (!proofFile) {
      alert('Por favor selecciona un comprobante');
      return;
    }

    setUploadingProof(true);

    try {
      const formData = new FormData();
      formData.append('proof', proofFile);
      
      

      const token = localStorage.getItem('token');
      
      const response = await fetch(
  `${process.env.REACT_APP_API_URL}/api/payments/verification/upload-proof`,
  {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  }
);

      // El backend explica por qué rechazó el archivo (tipo, tamaño): se muestra
      // eso en vez de un error genérico, así el paciente sabe qué cambiar.
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.proofUrl) {
        alert(`No se pudo subir el comprobante${data?.error ? `: ${data.error}` : ''}. Por favor intentá nuevamente.`);
        return;
      }

      onPaymentMethodSelected('transferencia', data.proofUrl);

    } catch (error) {
      console.error('Error:', error);
      alert('No se pudo subir el comprobante. Revisá tu conexión e intentá nuevamente.');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleMercadoPagoSubmit = async () => {
    setLoadingMercadoPago(true);

    try {
      // Determinar qué datos enviar según el flujo
      let requestData;
      
      if (appointmentId) {
        console.log('✅ Usando appointmentId existente:', appointmentId);
        requestData = { appointmentId };
      } else {
        const pendingBooking = sessionStorage.getItem('pendingBooking');
        if (!pendingBooking) {
          throw new Error('No hay datos de reserva pendiente');
        }

        const bookingData = JSON.parse(pendingBooking);
        console.log('✅ Usando datos de reserva pendiente:', bookingData);
        requestData = { bookingData };
      }

      // Crear preferencia de pago
      const token = localStorage.getItem('token');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/mercadopago/create-preference`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error creando preferencia de Mercado Pago');
      }

      const data = await response.json();
      console.log('✅ Preferencia creada:', data);

      // Redirigir directamente a Mercado Pago
      const checkoutUrl = data.initPoint;
      
      if (!checkoutUrl) {
        throw new Error('No se recibió URL de pago');
      }

      console.log('🔄 Redirigiendo a Mercado Pago...');
      
      // Redirigir en la misma ventana
      window.location.href = checkoutUrl;

    } catch (error: any) {
      console.error('Error:', error);
      alert(error.message || 'Error al procesar el pago');
      setLoadingMercadoPago(false);
    }
  };

  if (loadingConfig) {
    return <div className="text-center py-8">Cargando métodos de pago...</div>;
  }

  if (!paymentConfig?.acepta_transferencia && !paymentConfig?.acepta_mercadopago) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Este profesional no tiene métodos de pago configurados.</p>
        <button
          onClick={onBack}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          Volver
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={onBack}
          className="text-blue-600 hover:text-blue-700 flex items-center gap-2"
        >
          ← Volver
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-6">Selecciona el método de pago</h2>

      <div className="space-y-4">
        {/* Transferencia Bancaria */}
        {paymentConfig?.acepta_transferencia && (
          <div
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
              selectedMethod === 'transferencia'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedMethod('transferencia')}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Building2 className="w-7 h-7 text-blue-600" />
                <div>
                  <h3 className="font-semibold">Transferencia Bancaria</h3>
                  <p className="text-sm text-gray-600">Realiza una transferencia y sube el comprobante</p>
                </div>
              </div>
              {selectedMethod === 'transferencia' && (
                <div className="text-blue-500">✓</div>
              )}
            </div>

            {selectedMethod === 'transferencia' && (
              <div className="mt-4 pt-4 border-t">
                <div className="bg-gray-50 p-4 rounded mb-4">
                  <p className="font-semibold mb-2">Datos para la transferencia:</p>
                  {(paymentConfig.alias_cbu || paymentConfig.titular_cuenta) ? (
                    <div className="text-sm space-y-1">
                      {paymentConfig.alias_cbu && (
                        <p><span className="font-medium">Alias / CBU:</span> {paymentConfig.alias_cbu}</p>
                      )}
                      {paymentConfig.titular_cuenta && (
                        <p><span className="font-medium">Titular:</span> {paymentConfig.titular_cuenta}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600">Contacta al profesional para obtener los datos bancarios</p>
                  )}
                  
                  {/* Monto a pagar - CON SEÑA CORRECTA */}
                  <div className="mt-3 pt-3 border-t">
                    {isDeposit ? (
                      <>
                        <p className="font-bold text-lg text-blue-600">
                          Seña ({depositPercentage}%): ${paymentAmount}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          Precio total del servicio: ${fullPrice}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          El resto se abona en el consultorio
                        </p>
                      </>
                    ) : (
                      <p className="font-semibold text-lg">
                        Monto a pagar: ${paymentAmount}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700">
                      Subí el comprobante: captura, foto o PDF *
                    </span>
                    {/* El input del navegador queda oculto: al lado de su botón
                        escribe el nombre del archivo, y en el celular quedaba
                        una letra suelta ("C."). El archivo elegido ya se ve en
                        la miniatura o en la ficha del PDF. Al estar dentro del
                        label, tocar el botón de abajo abre el selector. */}
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleFileChange}
                      className="peer sr-only"
                    />
                    <span className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500">
                      <Upload className="w-4 h-4" />
                      {proofFile ? 'Cambiar archivo' : 'Elegir archivo'}
                    </span>
                  </label>

                  {/* La vista previa entra siempre en la tarjeta: antes tenía un
                      ancho fijo de 320 px y en el celular la tarjeta mide ~250,
                      así que una captura se salía por la derecha y ocupaba
                      700 px de alto. Ahora es una miniatura. */}
                  {proofPreview && (
                    <div className="mt-2 flex justify-center rounded-lg border bg-white p-2">
                      <img
                        src={proofPreview}
                        alt="Vista previa"
                        className="block max-h-64 w-auto max-w-full rounded object-contain"
                      />
                    </div>
                  )}

                  {proofFile?.type === 'application/pdf' && (
                    <div className="mt-2 flex items-center gap-2 rounded border bg-white px-3 py-2 text-left text-sm text-gray-700">
                      <FileText className="w-5 h-5 shrink-0 text-blue-600" />
                      {/* Un nombre largo se corta con "…" y se pierde el ".pdf":
                          abajo va el tipo y el peso */}
                      <div className="min-w-0">
                        <p className="truncate">{proofFile.name}</p>
                        <p className="text-xs text-gray-500">PDF · {tamanoLegible(proofFile.size)}</p>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleTransferSubmit}
                    disabled={!proofFile || uploadingProof || preparandoArchivo}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium
                      hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed
                      transition-colors"
                  >
                    {preparandoArchivo ? 'Preparando archivo...' : uploadingProof ? 'Subiendo...' : 'Confirmar pago'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mercado Pago */}
        {paymentConfig?.acepta_mercadopago && (
          <div
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
              selectedMethod === 'mercadopago'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedMethod('mercadopago')}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <CreditCard className="w-7 h-7 text-blue-600" />
                <div>
                  <h3 className="font-semibold">Mercado Pago</h3>
                  <p className="text-sm text-gray-600">Paga con tarjeta de crédito o débito</p>
                </div>
              </div>
              {selectedMethod === 'mercadopago' && (
                <div className="text-blue-500">✓</div>
              )}
            </div>

            {selectedMethod === 'mercadopago' && (
              <div className="mt-4 pt-4 border-t">
                {/* Monto a pagar - CON SEÑA CORRECTA */}
                {isDeposit ? (
                  <div className="mb-4">
                    <p className="text-lg font-bold text-blue-600">
                      Seña ({depositPercentage}%): ${paymentAmount}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Precio total del servicio: ${fullPrice}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      El resto se abona en el consultorio
                    </p>
                  </div>
                ) : (
                  <p className="text-lg font-semibold mb-4">
                    Monto a pagar: ${paymentAmount}
                  </p>
                )}
                
                <button
                  onClick={handleMercadoPagoSubmit}
                  disabled={loadingMercadoPago}
                  className="w-full bg-[#009EE3] text-white py-3 px-4 rounded-lg font-medium
                    hover:bg-[#0089CC] disabled:bg-gray-300 disabled:cursor-not-allowed
                    transition-colors flex items-center justify-center gap-2"
                >
                  {loadingMercadoPago ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      Pagar con Mercado Pago
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>

                {loadingMercadoPago && (
                  <p className="mt-3 text-sm text-gray-600 text-center">
                    Se abrirá una ventana de Mercado Pago. No la cierres hasta completar el pago.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {!selectedMethod && (
        <p className="text-center text-gray-500 mt-6">
          Selecciona un método de pago para continuar
        </p>
      )}
    </div>
  );
};

export default PaymentStep;